import { Node, Edge } from 'reactflow';
import { WorkflowCanvasMode } from './types';

export interface WorkflowContextMenuHandlers {
  // Node handlers
  onViewNodeDetails?: (id: string) => void;
  onShowDependencies?: (id: string) => void;
  onToggleExpand?: (id: string) => void;
  onAddConnected?: (id: string, direction: 'right' | 'bottom' | 'left' | 'top') => void;
  onDeleteNode?: (id: string) => void;
  onDuplicateNode?: (id: string) => void;
  onSetAsStart?: (id: string) => void;
  onEditDetails?: (id: string) => void;
  
  // Phase execution handlers
  onStartPhase?: (id: string) => void;
  onCompletePhase?: (id: string) => void;
  onSkipPhase?: (id: string) => void;
  onViewArtifacts?: (id: string) => void;
  
  // Edge handlers
  onChangeDependencyType?: (id: string, type: 'requires' | 'enhances' | 'blocks') => void;
  onReverseDirection?: (id: string) => void;
  onDeleteEdge?: (id: string) => void;
  
  // Canvas handlers
  onAddPhase?: (type: string, position: { x: number, y: number }) => void;
  onArrangeLayout?: () => void;
  onFitView?: () => void;
  onHighlightCriticalPath?: () => void;
  onChangeViewMode?: (mode: 'default' | 'timeline' | 'parallel') => void;
  onPauseExecution?: () => void;
  onResumeExecution?: () => void;
  onViewExecutionLog?: () => void;
  onViewAllArtifacts?: () => void;
}

export interface MenuItem {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
  type?: 'item' | 'header' | 'separator';
  icon?: React.ReactNode;
}

export class WorkflowContextMenuManager {
  private mode: WorkflowCanvasMode;
  private handlers: WorkflowContextMenuHandlers;
  private executionData?: any;
  private currentViewMode?: 'default' | 'timeline' | 'parallel';
  private phaseTypes?: Array<{ id: string, label: string }>;
  
  constructor(
    mode: WorkflowCanvasMode,
    handlers: WorkflowContextMenuHandlers,
    options?: {
      executionData?: any;
      currentViewMode?: 'default' | 'timeline' | 'parallel';
      phaseTypes?: Array<{ id: string, label: string }>;
    }
  ) {
    this.mode = mode;
    this.handlers = handlers;
    this.executionData = options?.executionData;
    this.currentViewMode = options?.currentViewMode;
    this.phaseTypes = options?.phaseTypes;
  }
  
  // Get phase status from execution data
  private getPhaseStatus(phaseId: string): string {
    if (!this.executionData || !this.executionData.phases) return 'not_started';
    const phase = this.executionData.phases.find((p: any) => p.id === phaseId);
    return phase ? phase.status : 'not_started';
  }
  
  // Check if phase dependencies are met
  private areDependenciesMet(phaseId: string): boolean {
    if (!this.executionData || !this.executionData.phases) return true;
    
    // Get all connections where this phase is the target
    const dependencies = (this.executionData.connections || [])
      .filter((conn: any) => conn.target === phaseId)
      .map((conn: any) => conn.source);
    
    // Check if all dependency phases are completed
    return dependencies.every((depId: string) => {
      const depPhase = this.executionData.phases.find((p: any) => p.id === depId);
      return depPhase && depPhase.status === 'completed';
    });
  }
  
  // Get context menu items for a node
  getNodeContextMenu(node: Node, closeMenu: () => void): MenuItem[] {
    const nodeId = node.id;
    const items: MenuItem[] = [];
    
    // VIEW mode items
    if (this.mode === WorkflowCanvasMode.VIEW) {
      if (this.handlers.onViewNodeDetails) {
        items.push({
          label: "View Phase Details",
          onClick: () => {
            this.handlers.onViewNodeDetails(nodeId);
            closeMenu();
          }
        });
      }
      
      if (this.handlers.onShowDependencies) {
        items.push({
          label: "Show Dependencies",
          onClick: () => {
            this.handlers.onShowDependencies(nodeId);
            closeMenu();
          }
        });
      }
      
      if (this.handlers.onToggleExpand) {
        items.push({
          label: node.data?.expanded ? "Collapse Details" : "Expand Details",
          onClick: () => {
            this.handlers.onToggleExpand(nodeId);
            closeMenu();
          }
        });
      }
    }
    
    // EDIT mode items
    if (this.mode === WorkflowCanvasMode.EDIT) {
      if (this.handlers.onAddConnected) {
        items.push({
          label: "Add Next Phase",
          onClick: () => {
            this.handlers.onAddConnected(nodeId, 'right');
            closeMenu();
          }
        });
        
        items.push({
          label: "Add Child Phase",
          onClick: () => {
            this.handlers.onAddConnected(nodeId, 'bottom');
            closeMenu();
          }
        });
        
        items.push({
          label: "Add Previous Phase",
          onClick: () => {
            this.handlers.onAddConnected(nodeId, 'left');
            closeMenu();
          }
        });
        
        items.push({
          label: "Add Parent Phase",
          onClick: () => {
            this.handlers.onAddConnected(nodeId, 'top');
            closeMenu();
          }
        });
        
        items.push({ type: 'separator' });
      }
      
      if (this.handlers.onEditDetails) {
        items.push({
          label: "Edit Phase Details",
          onClick: () => {
            this.handlers.onEditDetails(nodeId);
            closeMenu();
          }
        });
      }
      
      if (this.handlers.onDuplicateNode) {
        items.push({
          label: "Duplicate Phase",
          onClick: () => {
            this.handlers.onDuplicateNode(nodeId);
            closeMenu();
          }
        });
      }
      
      if (this.handlers.onSetAsStart) {
        items.push({
          label: "Set as Start Phase",
          onClick: () => {
            this.handlers.onSetAsStart(nodeId);
            closeMenu();
          }
        });
      }
      
      if (this.handlers.onDeleteNode) {
        items.push({ type: 'separator' });
        items.push({
          label: "Delete Phase",
          danger: true,
          onClick: () => {
            this.handlers.onDeleteNode(nodeId);
            closeMenu();
          }
        });
      }
    }
    
    // EXECUTE mode items
    if (this.mode === WorkflowCanvasMode.EXECUTE) {
      const status = this.getPhaseStatus(nodeId);
      const canStart = status === 'not_started' && this.areDependenciesMet(nodeId);
      const canComplete = status === 'in_progress';
      
      if (canStart && this.handlers.onStartPhase) {
        items.push({
          label: "Start Phase",
          onClick: () => {
            this.handlers.onStartPhase(nodeId);
            closeMenu();
          }
        });
      }
      
      if (canComplete && this.handlers.onCompletePhase) {
        items.push({
          label: "Complete Phase",
          onClick: () => {
            this.handlers.onCompletePhase(nodeId);
            closeMenu();
          }
        });
      }
      
      if (status === 'not_started' && this.handlers.onSkipPhase) {
        items.push({
          label: "Skip Phase",
          onClick: () => {
            this.handlers.onSkipPhase(nodeId);
            closeMenu();
          }
        });
      }
      
      items.push({ type: 'separator' });
      
      if (this.handlers.onViewNodeDetails) {
        items.push({
          label: "View Execution Details",
          onClick: () => {
            this.handlers.onViewNodeDetails(nodeId);
            closeMenu();
          }
        });
      }
      
      if (this.handlers.onViewArtifacts) {
        items.push({
          label: "View Phase Artifacts",
          onClick: () => {
            this.handlers.onViewArtifacts(nodeId);
            closeMenu();
          }
        });
      }
    }
    
    return items;
  }
  
  // Get context menu items for an edge
  getEdgeContextMenu(edge: Edge, closeMenu: () => void): MenuItem[] {
    const edgeId = edge.id;
    const items: MenuItem[] = [];
    
    // VIEW mode items
    if (this.mode === WorkflowCanvasMode.VIEW) {
      if (this.handlers.onViewNodeDetails) {
        items.push({
          label: "View Dependency Details",
          onClick: () => {
            this.handlers.onViewNodeDetails(edgeId);
            closeMenu();
          }
        });
      }
      
      if (this.handlers.onHighlightCriticalPath) {
        items.push({
          label: "Highlight Path",
          onClick: () => {
            this.handlers.onHighlightCriticalPath();
            closeMenu();
          }
        });
      }
    }
    
    // EDIT mode items
    if (this.mode === WorkflowCanvasMode.EDIT) {
      if (this.handlers.onChangeDependencyType) {
        items.push({
          label: "Dependency Type",
          type: 'header'
        });
        
        const currentType = edge.data?.type || 'requires';
        
        items.push({
          label: "Requires",
          disabled: currentType === 'requires',
          onClick: () => {
            this.handlers.onChangeDependencyType(edgeId, 'requires');
            closeMenu();
          }
        });
        
        items.push({
          label: "Enhances",
          disabled: currentType === 'enhances',
          onClick: () => {
            this.handlers.onChangeDependencyType(edgeId, 'enhances');
            closeMenu();
          }
        });
        
        items.push({
          label: "Blocks",
          disabled: currentType === 'blocks',
          onClick: () => {
            this.handlers.onChangeDependencyType(edgeId, 'blocks');
            closeMenu();
          }
        });
        
        items.push({ type: 'separator' });
      }
      
      if (this.handlers.onReverseDirection) {
        items.push({
          label: "Reverse Direction",
          onClick: () => {
            this.handlers.onReverseDirection(edgeId);
            closeMenu();
          }
        });
      }
      
      if (this.handlers.onDeleteEdge) {
        items.push({ type: 'separator' });
        items.push({
          label: "Delete Connection",
          danger: true,
          onClick: () => {
            this.handlers.onDeleteEdge(edgeId);
            closeMenu();
          }
        });
      }
    }
    
    // EXECUTE mode items
    if (this.mode === WorkflowCanvasMode.EXECUTE) {
      if (this.handlers.onViewNodeDetails) {
        items.push({
          label: "View Dependency Status",
          onClick: () => {
            this.handlers.onViewNodeDetails(edgeId);
            closeMenu();
          }
        });
      }
    }
    
    return items;
  }
  
  // Get context menu items for the canvas
  getCanvasContextMenu(position: { x: number, y: number }, closeMenu: () => void): MenuItem[] {
    const items: MenuItem[] = [];
    
    // VIEW mode items
    if (this.mode === WorkflowCanvasMode.VIEW) {
      if (this.handlers.onFitView) {
        items.push({
          label: "Fit View",
          onClick: () => {
            this.handlers.onFitView();
            closeMenu();
          }
        });
      }
      
      if (this.handlers.onHighlightCriticalPath) {
        items.push({
          label: "Toggle Critical Path",
          onClick: () => {
            this.handlers.onHighlightCriticalPath();
            closeMenu();
          }
        });
      }
      
      if (this.handlers.onChangeViewMode) {
        items.push({ type: 'separator' });
        items.push({
          label: "View Mode",
          type: 'header'
        });
        
        items.push({
          label: "Default View",
          disabled: this.currentViewMode === 'default',
          onClick: () => {
            this.handlers.onChangeViewMode('default');
            closeMenu();
          }
        });
        
        items.push({
          label: "Timeline View",
          disabled: this.currentViewMode === 'timeline',
          onClick: () => {
            this.handlers.onChangeViewMode('timeline');
            closeMenu();
          }
        });
        
        items.push({
          label: "Parallel View",
          disabled: this.currentViewMode === 'parallel',
          onClick: () => {
            this.handlers.onChangeViewMode('parallel');
            closeMenu();
          }
        });
      }
    }
    
    // EDIT mode items
    if (this.mode === WorkflowCanvasMode.EDIT) {
      if (this.handlers.onAddPhase && this.phaseTypes) {
        items.push({
          label: "Add Phase",
          type: 'header'
        });
        
        this.phaseTypes.forEach(phase => {
          items.push({
            label: phase.label,
            onClick: () => {
              this.handlers.onAddPhase(phase.id, position);
              closeMenu();
            }
          });
        });
        
        items.push({ type: 'separator' });
      }
      
      if (this.handlers.onArrangeLayout) {
        items.push({
          label: "Arrange Layout",
          onClick: () => {
            this.handlers.onArrangeLayout();
            closeMenu();
          }
        });
      }
      
      if (this.handlers.onFitView) {
        items.push({
          label: "Fit View",
          onClick: () => {
            this.handlers.onFitView();
            closeMenu();
          }
        });
      }
    }
    
    // EXECUTE mode items
    if (this.mode === WorkflowCanvasMode.EXECUTE) {
      const isPaused = this.executionData?.status === 'paused';
      
      if (isPaused && this.handlers.onResumeExecution) {
        items.push({
          label: "Resume Execution",
          onClick: () => {
            this.handlers.onResumeExecution();
            closeMenu();
          }
        });
      } else if (!isPaused && this.handlers.onPauseExecution) {
        items.push({
          label: "Pause Execution",
          onClick: () => {
            this.handlers.onPauseExecution();
            closeMenu();
          }
        });
      }
      
      items.push({ type: 'separator' });
      
      if (this.handlers.onViewExecutionLog) {
        items.push({
          label: "View Execution Log",
          onClick: () => {
            this.handlers.onViewExecutionLog();
            closeMenu();
          }
        });
      }
      
      if (this.handlers.onViewAllArtifacts) {
        items.push({
          label: "View All Artifacts",
          onClick: () => {
            this.handlers.onViewAllArtifacts();
            closeMenu();
          }
        });
      }
    }
    
    return items;
  }
}
